import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../components/SafeIcon';
import { 
  FiLayers, FiPlay, FiTarget, FiFilter, FiLink, FiNavigation, 
  FiCheckCircle, FiAlertCircle, FiChevronRight, FiClock,
  FiDatabase, FiMaximize2, FiSearch, FiCpu, FiInfo, FiHelpCircle, FiChevronDown
} from 'react-icons/fi';
import { sampleScans, sampleRecords, demoSteps, faqData } from '../data/demoMockData';

const FAQItem = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div className="border-b border-asphalt-700">
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className="w-full py-4 flex items-center justify-between text-left hover:text-transit-neon transition-colors"
      >
        <span className="text-sm font-bold text-gray-200">{question}</span>
        <SafeIcon icon={FiChevronDown} className={`transform transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div 
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <p className="pb-4 text-xs text-gray-400 leading-relaxed font-sans">{answer}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const LiveDemo = () => {
  const [activeStep, setActiveStep] = useState(1);
  const [simState, setSimState] = useState('success');
  const [searchQuery, setSearchQuery] = useState('');

  const filteredRecords = sampleRecords.filter(r => 
    r.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="max-w-7xl mx-auto space-y-12 pb-24 px-4 md:px-0">
      
      {/* 1. Overview / What it does */}
      <section className="p-8 bg-asphalt-800 border border-asphalt-600 rounded-lg relative overflow-hidden">
        <div className="absolute top-0 right-0 p-4 opacity-5 pointer-events-none">
          <SafeIcon icon={FiInfo} className="text-[12rem] text-transit-neon" />
        </div>
        <div className="relative z-10 max-w-2xl">
          <h1 className="text-3xl font-black text-white flex items-center glitch-text uppercase tracking-tighter">
            Overview / What it does
          </h1>
          <div className="mt-6 space-y-4 font-sans">
            <p className="text-gray-300 leading-relaxed">
              <strong className="text-amber-500 font-mono text-sm uppercase">The Mission:</strong> LocalLens is an urban discovery OS designed for modern explorers who are tired of algorithmic echo chambers. We solve the <span className="text-white font-semibold italic">"city fatigue"</span> problem by identifying real-time high-synergy locations.
            </p>
            <p className="text-gray-400 text-sm">
              Whether you're looking for a quiet workspace or a high-energy nightlife circuit, LocalLens scans the city's pulse to sequence your perfect urban journey.
            </p>
            <div className="flex flex-wrap gap-4 pt-2">
              <div className="flex items-center space-x-2 text-[10px] font-mono text-transit-neon">
                <SafeIcon icon={FiCheckCircle} /> <span>REAL-TIME SCANNING</span>
              </div>
              <div className="flex items-center space-x-2 text-[10px] font-mono text-transit-neon">
                <SafeIcon icon={FiCheckCircle} /> <span>P2P RECOMMENDATIONS</span>
              </div>
              <div className="flex items-center space-x-2 text-[10px] font-mono text-transit-neon">
                <SafeIcon icon={FiCheckCircle} /> <span>SEQUENCE PLANNING</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. Demo / How It Works */}
      <section className="space-y-6">
        <div className="flex items-center justify-between border-b border-asphalt-700 pb-2">
          <h2 className="text-xl font-black text-white uppercase tracking-tighter flex items-center">
            <SafeIcon icon={FiLayers} className="mr-3 text-amber-500" />
            Demo / How It Works
          </h2>
          <span className="text-[10px] font-mono text-gray-500 hidden sm:block">SYSTEM_MANUAL_v4.2</span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-1 space-y-2">
            <h3 className="text-xs font-mono text-amber-500 mb-4 px-2 uppercase tracking-widest">Try this flow</h3>
            {demoSteps.map((step) => (
              <button
                key={step.id}
                onClick={() => setActiveStep(step.id)}
                className={`w-full p-4 rounded-lg border text-left transition-all group ${
                  activeStep === step.id 
                    ? 'bg-transit-500/10 border-transit-neon text-white' 
                    : 'bg-asphalt-800 border-asphalt-700 text-gray-500 hover:border-gray-500'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-mono">0{step.id}_PHASE</span>
                  {activeStep === step.id && <SafeIcon icon={FiPlay} className="text-transit-neon animate-pulse" />}
                </div>
                <p className={`text-sm font-bold mt-1 ${activeStep === step.id ? 'text-white' : 'group-hover:text-gray-300'}`}>
                  {step.label}
                </p>
              </button>
            ))}
          </div>

          <div className="lg:col-span-2 bg-asphalt-800 border border-asphalt-600 rounded-lg p-8 flex flex-col items-center justify-center text-center min-h-[400px]">
             <AnimatePresence mode="wait">
               <motion.div
                 key={activeStep}
                 initial={{ opacity: 0, scale: 0.95 }}
                 animate={{ opacity: 1, scale: 1 }}
                 exit={{ opacity: 0, scale: 0.95 }}
                 className="max-w-md space-y-6"
               >
                 <div className="w-20 h-20 bg-asphalt-900 rounded-full flex items-center justify-center text-transit-neon mx-auto shadow-[0_0_30px_rgba(0,229,255,0.15)] border border-transit-neon/20">
                   <SafeIcon icon={[FiCpu, FiTarget, FiFilter, FiLink, FiNavigation, FiPlay][activeStep - 1]} className="text-3xl" />
                 </div>
                 <div>
                   <h4 className="text-2xl font-black text-white uppercase tracking-tight">{demoSteps[activeStep-1].label}</h4>
                   <p className="text-sm text-gray-400 mt-3 leading-relaxed">
                     {demoSteps[activeStep-1].description}
                   </p>
                 </div>
                 <div className="bg-asphalt-900 p-4 rounded border border-asphalt-700 font-mono text-[10px] text-left">
                   <p className="text-transit-neon mb-1">PROMPT_CMD_{activeStep}:</p>
                   <p className="text-gray-500 italic">"Execute {demoSteps[activeStep-1].label.toLowerCase()} with current GPS coordinates..."</p>
                 </div>
                 <button 
                   onClick={() => setActiveStep(prev => prev < 6 ? prev + 1 : 1)}
                   className="w-full py-3 bg-amber-500 hover:bg-amber-400 text-asphalt-900 font-bold uppercase text-xs tracking-widest rounded transition-all shadow-[0_0_20px_rgba(255,179,0,0.3)]"
                 >
                   {activeStep === 6 ? 'Reset Simulation' : 'Execute Next Phase'}
                 </button>
               </motion.div>
             </AnimatePresence>
          </div>
        </div>
      </section>

      {/* 3. Sample Data */}
      <section className="space-y-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-asphalt-700 pb-2">
          <h2 className="text-xl font-black text-white uppercase tracking-tighter flex items-center">
            <SafeIcon icon={FiDatabase} className="mr-3 text-transit-neon" />
            Sample Data
          </h2>
          <div className="relative">
            <input 
              type="text" 
              placeholder="SEARCH_DATABANKS..." 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="bg-asphalt-800 border border-asphalt-600 rounded px-10 py-2 text-xs font-mono text-white focus:outline-none focus:border-transit-neon w-full md:w-64"
            />
            <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* History Table */}
          <div className="lg:col-span-7 bg-asphalt-800 border border-asphalt-600 rounded-lg overflow-hidden">
            <div className="px-4 py-3 bg-asphalt-900 border-b border-asphalt-700 flex items-center justify-between">
              <span className="text-[10px] font-mono text-gray-400 uppercase tracking-widest">Global_Scan_Log</span>
              <div className="flex space-x-2">
                <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                <div className="w-2 h-2 rounded-full bg-amber-500" />
                <div className="w-2 h-2 rounded-full bg-green-500" />
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left font-mono text-[10px]">
                <thead>
                  <tr className="bg-asphalt-700/30 text-gray-500 uppercase">
                    <th className="px-4 py-3">ID_TAG</th>
                    <th className="px-4 py-3">SECTOR</th>
                    <th className="px-4 py-3">STATUS</th>
                    <th className="px-4 py-3 text-right">NODES</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-asphalt-700">
                  {sampleScans.map(scan => (
                    <tr key={scan.id} className="hover:bg-asphalt-700/50 transition-colors group">
                      <td className="px-4 py-4 text-white font-bold">{scan.id}</td>
                      <td className="px-4 py-4 text-gray-400">{scan.area}</td>
                      <td className="px-4 py-4">
                        <span className={`px-2 py-0.5 rounded border border-current text-[8px] font-bold ${
                          scan.status === 'COMPLETED' ? 'text-green-400' : scan.status === 'WARNING' ? 'text-amber-500' : 'text-transit-neon'
                        }`}>
                          {scan.status}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-right text-transit-neon">{scan.nodes}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Record Cards */}
          <div className="lg:col-span-5 space-y-4">
            <AnimatePresence mode="popLayout">
              {filteredRecords.length > 0 ? (
                filteredRecords.map((record, idx) => (
                  <motion.div 
                    layout
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    key={record.name}
                    className="p-4 bg-asphalt-800 border border-asphalt-600 rounded-lg flex space-x-4 hover:border-amber-500/50 transition-all cursor-crosshair group"
                  >
                    <div className="w-12 h-12 shrink-0 bg-asphalt-900 rounded border border-asphalt-700 flex items-center justify-center text-amber-500 group-hover:bg-amber-500 group-hover:text-asphalt-900 transition-all">
                      <SafeIcon icon={FiMaximize2} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between items-start">
                        <h5 className="text-sm font-black text-white uppercase truncate">{record.name}</h5>
                        <span className="text-[9px] font-mono text-transit-neon shrink-0">{record.synergy} SYNC</span>
                      </div>
                      <p className="text-[9px] font-mono text-gray-500 mt-1 uppercase tracking-widest">{record.category} // {record.date}</p>
                      <p className="text-xs text-gray-400 mt-2 line-clamp-1 italic">"{record.note}"</p>
                    </div>
                  </motion.div>
                ))
              ) : (
                <div className="h-full flex flex-col items-center justify-center p-12 border-2 border-dashed border-asphalt-700 rounded-lg text-gray-600 font-mono text-xs">
                  <SafeIcon icon={FiAlertCircle} className="text-3xl mb-3" />
                  NO_RECORDS_FOUND
                </div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </section>

      {/* 4. Simulated States */}
      <section className="space-y-6">
        <div className="flex items-center justify-between border-b border-asphalt-700 pb-2">
          <h2 className="text-xl font-black text-white uppercase tracking-tighter flex items-center">
            <SafeIcon icon={FiCpu} className="mr-3 text-amber-500" />
            Simulated States
          </h2>
        </div>

        <div className="bg-asphalt-800 border border-asphalt-600 rounded-lg p-6">
          <div className="flex flex-wrap gap-3 mb-8">
            {['before', 'in_progress', 'success', 'empty', 'error'].map(state => (
              <button
                key={state}
                onClick={() => setSimState(state)}
                className={`px-4 py-2 rounded font-mono text-[10px] uppercase tracking-widest transition-all ${
                  simState === state 
                    ? 'bg-transit-neon text-asphalt-900 font-black shadow-[0_0_15px_#00E5FF]' 
                    : 'bg-asphalt-700 text-gray-400 hover:bg-asphalt-600'
                }`}
              >
                {state}
              </button>
            ))}
          </div>

          <div className="bg-asphalt-900 border border-asphalt-700 rounded-lg p-12 min-h-[300px] flex flex-col items-center justify-center text-center">
             <AnimatePresence mode="wait">
               {simState === 'before' && (
                 <motion.div key="before" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-4">
                   <div className="w-16 h-16 border-2 border-dashed border-gray-700 rounded-full flex items-center justify-center mx-auto">
                     <SafeIcon icon={FiPlay} className="text-2xl text-gray-600" />
                   </div>
                   <p className="text-xs font-mono text-gray-500 uppercase tracking-widest">System_Armed // Awaiting_Trigger</p>
                 </motion.div>
               )}
               {simState === 'in_progress' && (
                 <motion.div key="progress" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-6">
                   <div className="relative w-20 h-20 mx-auto">
                     <div className="absolute inset-0 border-4 border-transit-neon/20 rounded-full" />
                     <div className="absolute inset-0 border-4 border-t-transit-neon rounded-full animate-spin" />
                   </div>
                   <div className="space-y-1">
                     <p className="text-xs font-mono text-transit-neon uppercase animate-pulse">Scanning_Uplink...</p>
                     <p className="text-[10px] font-mono text-gray-600 uppercase">Latency: 14ms // Packets: 402/1024</p>
                   </div>
                 </motion.div>
               )}
               {simState === 'success' && (
                 <motion.div key="success" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="space-y-4">
                   <div className="w-20 h-20 bg-green-500/10 border border-green-500/30 rounded-full flex items-center justify-center mx-auto text-green-400">
                     <SafeIcon icon={FiCheckCircle} className="text-4xl" />
                   </div>
                   <div>
                     <p className="text-xs font-mono text-green-400 uppercase font-black">Handshake_Complete</p>
                     <p className="text-sm text-white mt-2 font-bold tracking-tight">Discovery vector successfully updated.</p>
                   </div>
                 </motion.div>
               )}
               {simState === 'empty' && (
                 <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-4 text-gray-500">
                   <SafeIcon icon={FiSearch} className="text-5xl mx-auto opacity-20" />
                   <p className="text-xs font-mono uppercase">Zero_Nodes_Detected</p>
                   <p className="text-[10px] italic">Try adjusting your budget or sector targeting.</p>
                 </motion.div>
               )}
               {simState === 'error' && (
                 <motion.div key="error" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} className="space-y-4">
                   <div className="w-16 h-16 bg-red-500/10 border border-red-500/30 rounded-full flex items-center justify-center mx-auto text-red-500">
                     <SafeIcon icon={FiAlertCircle} className="text-3xl" />
                   </div>
                   <div>
                     <p className="text-xs font-mono text-red-500 uppercase font-black">Critical_Failure</p>
                     <p className="text-[10px] font-mono text-gray-500 mt-1 uppercase">Error_Code: X-GRID-OFFLINE-009</p>
                   </div>
                 </motion.div>
               )}
             </AnimatePresence>
          </div>
        </div>
      </section>

      {/* 5. FAQ */}
      <section className="space-y-6">
        <div className="flex items-center space-x-3 border-b border-asphalt-700 pb-2">
          <h2 className="text-xl font-black text-white uppercase tracking-tighter flex items-center">
            <SafeIcon icon={FiHelpCircle} className="mr-3 text-amber-500" />
            FAQ
          </h2>
        </div>
        <div className="bg-asphalt-800 border border-asphalt-600 rounded-lg p-6 md:p-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12">
            {faqData.map((faq, idx) => (
              <FAQItem key={idx} question={faq.q} answer={faq.a} />
            ))}
          </div>
        </div>
      </section>

      <footer className="mt-20 pt-12 border-t border-asphalt-700 flex flex-col items-center space-y-6">
        <div className="flex space-x-8 text-[10px] font-mono text-gray-500 tracking-[0.4em] uppercase">
          <span className="flex items-center"><span className="w-1.5 h-1.5 bg-green-500 rounded-full mr-2" /> ONLINE</span>
          <span>BUILD: 1.4.0-BETA</span>
          <span>NODES: 1,402</span>
        </div>
        <p className="text-[11px] font-mono text-gray-600 tracking-[0.2em] uppercase font-bold">
          Made by LingLink AI and DigLab AI
        </p>
      </footer>
    </div>
  );
};

export default LiveDemo;